import { Box } from '@chakra-ui/react'
import { RectangleMeshProps } from '@editor/interfaces/canvas/rectangleMesh'
import CircleAnchor from '@editor/pages/projects/editor/objects/EditorCircleAnchor'
import { RootState } from '@editor/store/app'
import { StylesProvider } from '@infraloop/common'
import { Html } from '@react-three/drei'
import { useSelector } from 'react-redux'
import { DoubleSide } from 'three'

import useAdjustmentHandlers from './hooks/useAdjustmentHandlers'
import useAnchorEventHandlers from './hooks/useAnchorEventsHandler'
import useEventHandlers from './hooks/useEventsHandler'
import useObjectsManagement from './hooks/useObjectsManagement'
import useParams from './hooks/useParams'

const RectangleMesh = (props: RectangleMeshProps) => {
  const {
    id,
    color = '#ffffff',
    opacity = 1,
    label,

    visible = true,
    selectable = true,

    renderOrder,
    depthTest = true,
    depthWrite = true,

    isWorking,
    vertexAnchors,
  } = props

  // Store
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)
  const hoveredElementId = useSelector((state: RootState) => state.toolUtil.core.hoveredElementId)

  // Vars
  const isHovered = hoveredElementId === id
  const isSelected = selectedElementIds.includes(id)
  const isHoverSelected = isHovered || isSelected

  /**
   * Event handlers
   */
  const params = useParams(props)
  const objects = useObjectsManagement(props)
  const { movingStartLocalPointRef, isDraggingRef, isAdjustingAnchorRef } = useAdjustmentHandlers(
    props,
    objects,
    params,
  )
  const { position, quaternion, labelConfig } = params
  const { rectangleGeometry, outlineObject, meshRef, anchorPositions, offsetPosition } = objects

  const { handleMouseEnter, handleMouseLeave, handleMouseDown, handleClick } = useEventHandlers(
    props,
    isDraggingRef,
    movingStartLocalPointRef,
  )
  const { hoveredAnchorId, handleAnchorMouseEnter, handleAnchorMouseLeave, handleAnchorMouseDown } =
    useAnchorEventHandlers(props, isAdjustingAnchorRef)

  return (
    <group visible={opacity > 0 && visible} name={`rectangle-mesh-${id}`} position={offsetPosition}>
      {vertexAnchors?.visible && !isWorking && selectable && anchorPositions.length > 0 && (
        <group>
          {anchorPositions.map(({ id: anchorId, position: pos }, anchorIndex) => (
            <CircleAnchor
              key={`rectangle-${id}-anchor-${anchorId}`}
              id={`rectangle-${id}-anchor-${anchorId}`}
              point={pos.toArray()}
              color="red"
              outlineColor="white"
              outlineOpacity={isHoverSelected || hoveredAnchorId === anchorId ? 1 : 0}
              opacity={isHoverSelected || hoveredAnchorId === anchorId ? 1 : 0} // use opacity instead of visible to retain mouse events
              onEnter={selectable ? handleAnchorMouseEnter(anchorId) : undefined}
              onLeave={selectable ? handleAnchorMouseLeave : undefined}
              onDown={selectable ? handleAnchorMouseDown(anchorIndex) : undefined}
              renderOrder={(renderOrder || 0) + 1}
            />
          ))}
        </group>
      )}

      {rectangleGeometry && (
        <mesh
          ref={meshRef}
          geometry={rectangleGeometry}
          position={position}
          quaternion={quaternion}
          visible={color !== 'transparent'}
          onPointerEnter={selectable ? handleMouseEnter : undefined}
          onPointerLeave={handleMouseLeave} // Always allow mouse leave to reset hover state
          onPointerDown={selectable ? handleMouseDown : undefined}
          onClick={selectable ? handleClick : undefined}
          renderOrder={renderOrder}
        >
          <meshStandardMaterial
            color={color === 'transparent' ? 'white' : color}
            opacity={opacity}
            transparent
            side={DoubleSide}
            depthTest={depthTest}
            depthWrite={depthWrite}
          />
        </mesh>
      )}

      {/* Outline */}
      {outlineObject && <primitive object={outlineObject} position={position} quaternion={quaternion} />}

      {/* Label */}
      {!!label && labelConfig ? (
        <Html
          position={position}
          style={{ transform: 'translateX(-50%) translateY(-50%)', pointerEvents: 'none' }}
          zIndexRange={[1, 9]}
        >
          <StylesProvider>
            <Box
              backgroundColor={labelConfig.backgroundColor}
              px={2}
              fontSize={`${80 * (labelConfig.fontSize || 1)}%`}
              fontWeight="bold"
              color={labelConfig.color}
              whiteSpace="nowrap"
            >
              {labelConfig.message}
            </Box>
          </StylesProvider>
        </Html>
      ) : null}
    </group>
  )
}

export default RectangleMesh
